import { Check, X } from "lucide-react";
import { TIERS } from "@/data/content";
import { Reveal, SectionLabel, CtaButton } from "@/components/landing/shared";

const COMPARE = [
  { label: "Price per ad", trad: "$1,000 – $2,500", fn: "$250 – $350" },
  { label: "Turnaround", trad: "3 – 6 weeks", fn: "Free sample in 24h" },
  { label: "Filming & studio", trad: "Required", fn: "None — pure motion" },
  { label: "Upfront risk", trad: "100% committed", fn: "Pay only if you love it" },
];

export const Pricing = () => (
  <section id="pricing" className="border-t border-zinc-900 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <SectionLabel index="03" testid="pricing-label">
          Pricing
        </SectionLabel>
        <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-none tracking-tight sm:text-5xl">
          Fair pricing. Zero risk.
        </h2>
      </Reveal>

      {/* Value comparison */}
      <Reveal delay={0.05}>
        <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-zinc-900 bg-zinc-900 md:grid-cols-2">
          <div className="bg-[#080808] p-8" data-testid="compare-traditional">
            <p className="ff-mono text-xs uppercase tracking-widest text-zinc-600">Traditional studios</p>
            <ul className="mt-6 space-y-4">
              {COMPARE.map((c) => (
                <li key={c.label} className="flex items-start gap-3">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-zinc-600" />
                  <span className="text-sm text-zinc-500">
                    <span className="text-zinc-400">{c.label}:</span> {c.trad}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative bg-[#0f0f0f] p-8" data-testid="compare-flownavy">
            <p className="ff-mono text-xs uppercase tracking-widest text-zinc-300">Flownavy</p>
            <ul className="mt-6 space-y-4">
              {COMPARE.map((c) => (
                <li key={c.label} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-white" />
                  <span className="text-sm text-zinc-300">
                    <span className="text-white">{c.label}:</span> {c.fn}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-xl border border-zinc-800 bg-white/[0.03] px-4 py-3">
              <p className="ff-mono text-xs text-zinc-300">
                First 50 brands only — portfolio-building phase. Price rises after.
              </p>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Tiers */}
      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        {TIERS.map((t, i) => {
          const featured = !!t.badge;
          return (
            <Reveal key={t.name} delay={i * 0.08}>
              <div
                data-testid={`tier-${i}`}
                className={`flex h-full flex-col rounded-2xl border p-8 transition-colors duration-300 ${
                  featured
                    ? "border-[#C7FF3C] bg-white text-black"
                    : "border-zinc-800 bg-[#0a0a0a] text-white hover:border-zinc-600"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className={`text-lg font-semibold ${featured ? "text-black" : "text-white"}`}>
                    {t.name}
                  </h3>
                  {t.badge && (
                    <span className="rounded-full bg-[#C7FF3C] px-3 py-1 ff-mono text-[10px] uppercase tracking-widest text-black">
                      {t.badge}
                    </span>
                  )}
                </div>
                <p className={`mt-1 text-sm ${featured ? "text-zinc-600" : "text-zinc-500"}`}>
                  {t.subtitle}
                </p>
                <p className="mt-6 font-display text-5xl font-extrabold tracking-tight">{t.price}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check
                        className={`mt-0.5 h-4 w-4 shrink-0 ${featured ? "text-black" : "text-zinc-400"}`}
                      />
                      <span className={`text-sm ${featured ? "text-zinc-700" : "text-zinc-400"}`}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
                <CtaButton
                  testid={`tier-cta-${i}`}
                  variant={featured ? "solid" : "outline"}
                  className={`mt-8 w-full justify-center ${featured ? "!bg-black !text-white hover:!bg-zinc-800" : ""}`}
                >
                  Start with free sample
                </CtaButton>
              </div>
            </Reveal>
          );
        })}
      </div>

      <Reveal delay={0.1}>
        <p className="mt-8 text-center ff-mono text-xs text-zinc-500" data-testid="pricing-note">
          Every project starts with a free 24-hour sample. Pay only if you want it.
        </p>
      </Reveal>
    </div>
  </section>
);
